import { type ReactNode } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth, type Persona } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { Elections } from './pages/elections/Elections';
import { ElectionDetail } from './pages/elections/ElectionDetail';
import { Geography } from './pages/elections/Geography';
import { Parties } from './pages/elections/Parties';
import { VoterRoll } from './pages/elections/VoterRoll';
import { RollRevisions } from './pages/elections/RollRevisions';
import { RollRevisionDetail } from './pages/elections/RollRevisionDetail';
import { Nominations } from './pages/elections/Nominations';
import { NominationDetail } from './pages/elections/NominationDetail';
import { Tallies } from './pages/elections/Tallies';
import { TallyDetail } from './pages/elections/TallyDetail';
import { Results } from './pages/elections/Results';
import { ResultDetail } from './pages/elections/ResultDetail';
import { PublicResults } from './pages/elections/PublicResults';
import { Complaints } from './pages/ombudsman/Complaints';
import { ComplaintDetail } from './pages/ombudsman/ComplaintDetail';
import { Whistleblower } from './pages/ombudsman/Whistleblower';
import { WhistleblowerDetail } from './pages/ombudsman/WhistleblowerDetail';
import { PublicWhistleblowerIntake } from './pages/ombudsman/PublicWhistleblowerIntake';
import { IntegrityCases } from './pages/ombudsman/IntegrityCases';
import { IntegrityCaseDetail } from './pages/ombudsman/IntegrityCaseDetail';
import { SystemicIssues } from './pages/ombudsman/SystemicIssues';
import { SystemicIssueDetail } from './pages/ombudsman/SystemicIssueDetail';
import { AssetDeclarations } from './pages/ombudsman/AssetDeclarations';
import { AssetDeclarationDetail } from './pages/ombudsman/AssetDeclarationDetail';
import { Shell, type NavGroup } from './ui/Shell';

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Electoral Commission (M39)',
    items: [
      { to: '/elections', label: 'Elections', icon: '🗳' },
      { to: '/elections/geography', label: 'Geography', icon: '🗺' },
      { to: '/elections/parties', label: 'Parties', icon: '🏛' },
      { to: '/elections/roll', label: 'Voter Roll', icon: '📋' },
      { to: '/elections/roll-revisions', label: 'Roll Revisions', icon: '📝' },
      { to: '/elections/nominations', label: 'Nominations', icon: '🖊' },
      { to: '/elections/tallies', label: 'Tallies', icon: '🔢' },
      { to: '/elections/results', label: 'Results', icon: '📊' },
      { to: '/public/results', label: 'Public Results Feed', icon: '🌐' },
    ],
  },
  {
    label: 'Ombudsman & ACCS (M40)',
    items: [
      { to: '/ombudsman/complaints', label: 'Complaints', icon: '📮' },
      { to: '/ombudsman/whistleblower', label: 'Whistleblower', icon: '🕵' },
      { to: '/ombudsman/integrity-cases', label: 'Integrity Cases', icon: '🔒' },
      { to: '/ombudsman/systemic-issues', label: 'Systemic Issues', icon: '📈' },
      { to: '/ombudsman/asset-declarations', label: 'Asset Declarations', icon: '💰' },
    ],
  },
];

const ELECTIONS_ROLES = ['ELECTORAL_COMMISSIONER', 'REGISTRATION_OFFICER', 'RETURNING_OFFICER'];

/** Where to land a freshly-signed-in persona — their primary workspace, not always the same page. */
function landingFor(persona: Persona): string {
  const r = persona.roles;
  if (r.includes('OMBUDSMAN_OFFICER')) return '/ombudsman/complaints';
  if (r.includes('ACCS_INVESTIGATOR')) return '/ombudsman/integrity-cases';
  if (r.includes('DECLARATION_REGISTRAR')) return '/ombudsman/asset-declarations';
  if (r.some((role) => ELECTIONS_ROLES.includes(role))) return '/elections';
  // Self-service persona (no MDA role) — the voter-roll self-check is the
  // most broadly relevant landing; asset-declaration self-service is one
  // nav click away under Ombudsman & ACCS.
  return '/elections/roll';
}

/**
 * Minimal chrome for the genuinely public M39/M40 surfaces — no sidebar, no
 * persona, reachable whether or not this browser also happens to have a
 * staff session (both routes are defined once, outside the persona branch
 * below, specifically so a signed-in officer sees exactly what an
 * unauthenticated citizen sees when they follow the same link).
 */
function PublicChrome({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white px-6 py-3.5">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <Link to="/public/results" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-xs font-black text-white">SC</span>
            <span className="text-sm font-bold text-slate-900">One Gov — Governance &amp; Independent Institutions (Public)</span>
          </Link>
          <Link to="/" className="text-xs font-semibold text-brand-700 hover:underline">
            Staff sign-in →
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-7">{children}</main>
    </div>
  );
}

export function App() {
  const { persona } = useAuth();

  return (
    <Routes>
      <Route path="/signin/complete" element={<SignInComplete />} />

      {/* Genuinely public, unauthenticated M39/M40 surfaces (each module's
          own app.ts mounts these before requireAuth) — defined once, outside
          the persona branch, so they behave identically for a citizen with
          no session and a staff member who happens to be signed in on this
          device. See m39-elections/src/routes/public.ts and
          m40-ombudsman/src/routes/whistleblowerPublic.ts. */}
      <Route
        path="/public/results"
        element={
          <PublicChrome>
            <PublicResults />
          </PublicChrome>
        }
      />
      <Route
        path="/report-corruption"
        element={
          <PublicChrome>
            <PublicWhistleblowerIntake />
          </PublicChrome>
        }
      />

      {!persona ? (
        <Route path="*" element={<Login />} />
      ) : (
        <Route element={<Shell title="Governance & Independent Institutions" groups={NAV_GROUPS} />}>
          <Route index element={<Navigate to={landingFor(persona)} replace />} />

          <Route path="elections/geography" element={<Geography />} />
          <Route path="elections/parties" element={<Parties />} />
          <Route path="elections/roll" element={<VoterRoll />} />
          <Route path="elections/roll-revisions" element={<RollRevisions />} />
          <Route path="elections/roll-revisions/:id" element={<RollRevisionDetail />} />
          <Route path="elections/nominations" element={<Nominations />} />
          <Route path="elections/nominations/:id" element={<NominationDetail />} />
          <Route path="elections/tallies" element={<Tallies />} />
          <Route path="elections/tallies/:id" element={<TallyDetail />} />
          <Route path="elections/results" element={<Results />} />
          <Route path="elections/results/:id" element={<ResultDetail />} />
          <Route path="elections" element={<Elections />} />
          <Route path="elections/:id" element={<ElectionDetail />} />

          <Route path="ombudsman/complaints" element={<Complaints />} />
          <Route path="ombudsman/complaints/:id" element={<ComplaintDetail />} />
          <Route path="ombudsman/whistleblower" element={<Whistleblower />} />
          <Route path="ombudsman/whistleblower/:id" element={<WhistleblowerDetail />} />
          <Route path="ombudsman/integrity-cases" element={<IntegrityCases />} />
          <Route path="ombudsman/integrity-cases/:id" element={<IntegrityCaseDetail />} />
          <Route path="ombudsman/systemic-issues" element={<SystemicIssues />} />
          <Route path="ombudsman/systemic-issues/:id" element={<SystemicIssueDetail />} />
          <Route path="ombudsman/asset-declarations" element={<AssetDeclarations />} />
          <Route path="ombudsman/asset-declarations/:id" element={<AssetDeclarationDetail />} />

          <Route path="*" element={<Navigate to={landingFor(persona)} replace />} />
        </Route>
      )}
    </Routes>
  );
}
